import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Save } from "lucide-react";
import { api } from "../../lib/api";
import {
  BrutalButton,
  BrutalCard,
  BrutalInput,
  BrutalLabel,
  BrutalTextarea,
  Badge,
  SectionTitle,
} from "../../components/common/Brutal";

const empty = {
  name: "",
  date: "",
  start_time: "",
  end_time: "",
  location: "",
  description: "",
  category: "music",
};

export default function AdminActivities() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/activities").then(({ data }) => setItems(data));

  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing("new");
    setForm(empty);
  };

  const startEdit = (a) => {
    setEditing(a.id);
    setForm({ ...a });
  };

  const cancel = () => {
    setEditing(null);
    setForm(empty);
  };

  const save = async () => {
    try {
      if (editing === "new") {
        await api.post("/activities", form);
        toast.success("Activity added");
      } else {
        await api.put(`/activities/${editing}`, form);
        toast.success("Activity updated");
      }
      cancel();
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this activity?")) return;
    await api.delete(`/activities/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-activities">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <SectionTitle kicker="Activity Manager" title="Festival schedule" />
        {!editing && (
          <BrutalButton
            variant="primary"
            onClick={startNew}
            data-testid="activity-add"
          >
            <Plus className="h-4 w-4" /> Add Activity
          </BrutalButton>
        )}
      </div>

      {editing && (
        <BrutalCard className="p-5" data-testid="activity-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Name">
              <BrutalInput
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="activity-name"
              />
            </Field>
            <Field label="Category">
              <select
                className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5 text-base font-medium"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="activity-category"
              >
                {["music", "ceremony", "workshop", "wellness", "food"].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label="Date">
              <BrutalInput
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="activity-date"
              />
            </Field>
            <Field label="Location">
              <BrutalInput
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                data-testid="activity-location"
              />
            </Field>
            <Field label="Start Time">
              <BrutalInput
                type="time"
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                data-testid="activity-start"
              />
            </Field>
            <Field label="End Time">
              <BrutalInput
                type="time"
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                data-testid="activity-end"
              />
            </Field>
            <div className="sm:col-span-2">
              <BrutalLabel>Description</BrutalLabel>
              <BrutalTextarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="activity-description"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <BrutalButton variant="primary" onClick={save} data-testid="activity-save">
              <Save className="h-4 w-4" /> Save
            </BrutalButton>
            <BrutalButton variant="outline" onClick={cancel} data-testid="activity-cancel">
              <X className="h-4 w-4" /> Cancel
            </BrutalButton>
          </div>
        </BrutalCard>
      )}

      <div className="space-y-3">
        {items.map((a) => (
          <BrutalCard key={a.id} className="p-4" data-testid={`activity-row-${a.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge color="orange">{a.date} · {a.start_time}–{a.end_time}</Badge>
                  <Badge color="yellow">{a.category}</Badge>
                </div>
                <h4 className="display text-2xl mt-2">{a.name}</h4>
                <p className="text-sm text-[#6B4E9E]">{a.location} · {a.description}</p>
              </div>
              <div className="flex gap-2">
                <BrutalButton
                  variant="outline"
                  className="px-3 py-2 text-xs"
                  onClick={() => startEdit(a)}
                  data-testid={`activity-edit-${a.id}`}
                >
                  <Pencil className="h-3 w-3" /> Edit
                </BrutalButton>
                <BrutalButton
                  variant="dark"
                  className="px-3 py-2 text-xs"
                  onClick={() => remove(a.id)}
                  data-testid={`activity-delete-${a.id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </BrutalButton>
              </div>
            </div>
          </BrutalCard>
        ))}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <BrutalLabel>{label}</BrutalLabel>
    {children}
  </div>
);
